import { useMap } from "@vis.gl/react-google-maps";
import React, { useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { MAP_ELEMENT } from "../../../contexts/WorldMapContext/constants";
import { createVesselUpdates, sameVesselAppearance } from "../../../contexts/WorldMapContext/vesselUpdates";
import type { VesselUpdates } from "../../../contexts/WorldMapContext/vesselUpdates";
import { IPropsMarker, VesselMarker } from "../../../contexts/WorldMapContext/Marker";
import { selectDashboard, selectOneSetting } from "../../../util/store/selectors";
import { DashboardSetting } from "../../../util/varibles/userSetting";
import { dashboardActions } from "../reducer";


interface IProps {
    ids: Set<string>
}

const Vessels: React.FC<IProps> = ({ ids }) => {
    const filterData = useSelector(selectOneSetting(DashboardSetting.VESSELS));
    const filter = useMemo(() => new Set(filterData), [filterData]);
    const source = useSelector(selectDashboard.vessels);
    const cache = useRef(new Map<string, { data: IPropsMarker['data']; updates: VesselUpdates }>());
    const rows = useMemo(() => source.map(item => {
        const previous = cache.current.get(item.id);
        return {
            id: item.id,
            data: previous && sameVesselAppearance(previous.data, item) ? previous.data : item,
            updates: previous?.updates || createVesselUpdates(item),
            latest: item
        };
    }), [source]);

    useLayoutEffect(() => {
        cache.current = new Map(rows.map(row => [row.id, row]));
        rows.forEach(row => row.updates.update(row.latest));
    }, [rows]);

    const target = useSelector(selectDashboard.target);
    const tracking = useSelector(selectDashboard.tracking);
    const map = useMap();
    const dispatch = useDispatch();

    const lockId = useMemo(() => {
        if (target.targetType === MAP_ELEMENT.VESSEL && tracking) {
            const { Latitude, Longitude } = target.data.gps;
            map?.setZoom(7)
            map?.setCenter({ lat: Latitude, lng: Longitude });
            return target.data.id;
        } else
            return undefined;
    }, [target, tracking, map]);

    const hasFilter = useMemo(() => filter.size > 0 && source.some(item => filter.has(item.id)), [filter, source]);

    const onClick = useCallback((data: IPropsMarker['data']) => {
        dispatch(dashboardActions.showInfo({ data, targetType: MAP_ELEMENT.VESSEL }));
    }, [dispatch])

    const showAll = ids.size === 0 && !hasFilter;

    return <>
        {rows.map(({ id, data, updates }) => {
            return <VesselMarker
                key={id}
                data={data}
                updates={updates}
                show={ids.has(id) || (hasFilter && filter.has(id)) || showAll}
                onSelect={onClick}
                showPopup={lockId === id}
            />
        })}
    </>;
};

export default Vessels
